/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useEffect, useState } from 'react';
import styles from './UpCharges.module.css';
import Dropdown from '@/components/Dropdown/Dropdown';
import Card from '@/components/Card/Card';
import Table from '@/components/Tables/Table/Table';
import { BundleDetails } from '@/types/bundles.types';
import { EditUpchargeInput } from '../EditPrices/EditUpchargeInput';
import { EditUpcharge } from '../EditPrices/EditUpcharge';
import { UpChargesProps } from '../CreateLinkedBundles.types';

const columns = [
  {
    accessorKey: 'bundleCode',
    header: 'Bundle Code',
    accessorFn: (originalRow: BundleDetails) => originalRow.code || '-',
  },
  {
    accessorKey: 'displayName',
    header: 'Display Name',
    accessorFn: (originalRow: BundleDetails) => originalRow.name || '-',
  },
  {
    accessorKey: 'bundleDescription',
    header: 'Bundle Desc.',
    accessorFn: (originalRow: BundleDetails) => originalRow.internalName || '-',
  },
  {
    accessorKey: 'upChargePrice',
    header: 'Upcharge',
    cell: EditUpchargeInput,
  },
  {
    accessorKey: '',
    header: 'Edit',
    cell: EditUpcharge,
  } as any,
];

export const UpCharges = (props: UpChargesProps) => {
  const {
    selectedBundles,
    availablePrices,
    onPriceChange,
    setPriceValues,
    linkedBundleFormValues,
    setLinkedBundleFormValues,
  } = props;

  const [validRows, setValidRows] = useState({});
  const [cancelEdition, setCancelEdition] = useState({});
  const [editedRows, setEditedRows] = useState({});

  const priceOptions = availablePrices?.map((price: string) => ({
    value: Number(price).toFixed(2) || '',
    label: `$${Number(price).toFixed(2)}`,
  }));
  const defaultDropdownPriceOptions = [{ value: '', label: 'Select' }, ...(priceOptions || [])];

  const [data, setData] = useState(selectedBundles);
  const [originalData, setOriginalData] = useState(selectedBundles);
  const initialFormValues: { [string: string]: string } = {
    pricePoint1: '',
    pricePoint2: '',
    pricePoint3: '',
    pricePoint4: '',
  };

  const [formValues, setFormValues] = useState<{ [string: string]: string }>({
    pricePoint1: '',
    pricePoint2: '',
    pricePoint3: '',
    pricePoint4: '',
  });

  const dropdownOneRequiresPrice = availablePrices && availablePrices.length >= 1 && formValues.pricePoint1 === '';
  const dropdownTwoRequiresPrice = availablePrices && availablePrices.length >= 2 && formValues.pricePoint2 === '';
  const dropdownThreeRequiresPrice = availablePrices && availablePrices.length >= 3 && formValues.pricePoint3 === '';
  const dropdownFourRequiresPrice = availablePrices && availablePrices.length >= 4 && formValues.pricePoint4 === '';

  const duplicatePriceText = 'Price already selected';
  const enterPriceText = 'Enter price';
  const [formErrors, setFormErrors] = useState<{ [string: string]: boolean }>({
    pricePoint1: false,
    pricePoint2: false,
    pricePoint3: false,
    pricePoint4: false,
  });

  useEffect(() => {
    if (
      !formErrors.pricePoint1 &&
      !formErrors.pricePoint2 &&
      !formErrors.pricePoint3 &&
      !formErrors.pricePoint4 &&
      !dropdownOneRequiresPrice &&
      !dropdownTwoRequiresPrice &&
      !dropdownThreeRequiresPrice &&
      !dropdownFourRequiresPrice
    ) {
      onPriceChange(true);
      setPriceValues({
        price: formValues,
        selectedBundleData: data,
      });
      setLinkedBundleFormValues({
        ...linkedBundleFormValues,
        pricePoint1: formValues.pricePoint1,
        pricePoint2: formValues.pricePoint2,
        pricePoint3: formValues.pricePoint3,
        pricePoint4: formValues.pricePoint4,
      });
    } else {
      onPriceChange(false);
    }
  }, [
    dropdownFourRequiresPrice,
    dropdownOneRequiresPrice,
    dropdownThreeRequiresPrice,
    dropdownTwoRequiresPrice,
    formErrors,
    onPriceChange,
    data,
    formValues,
    setPriceValues,
    setLinkedBundleFormValues,
    linkedBundleFormValues.pricePoint1,
    linkedBundleFormValues.pricePoint2,
    linkedBundleFormValues.pricePoint3,
    linkedBundleFormValues.pricePoint4,
  ]);

  const handleDropdownSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: !event.target.value ? '' : event.target.value });
    if (Object.values(formValues).includes(event.target.value)) {
      setFormErrors({ ...formErrors, [event.target.id]: true });
    } else {
      setFormErrors({ ...formErrors, [event.target.id]: false });
    }
  };

  const dropdownOneError = () => {
    if (dropdownOneRequiresPrice) {
      return enterPriceText;
    }
    if (!formErrors.pricePoint1) {
      return undefined;
    }
    return duplicatePriceText;
  };
  const dropdownTwoError = () => {
    if (dropdownTwoRequiresPrice) {
      return enterPriceText;
    }
    if (!formErrors.pricePoint2) {
      return undefined;
    }
    return duplicatePriceText;
  };
  const dropdownThreeError = () => {
    if (dropdownThreeRequiresPrice) {
      return enterPriceText;
    }
    if (!formErrors.pricePoint3) {
      return undefined;
    }
    return duplicatePriceText;
  };
  const dropdownFourError = () => {
    if (dropdownFourRequiresPrice) {
      return enterPriceText;
    }
    if (!formErrors.pricePoint4) {
      return undefined;
    }
    return duplicatePriceText;
  };

  return (
    <div className={styles.stepThree}>
      <div className={styles.stepThreeAccordionContainer}>
        <Card>
          <h3 className={styles.title}>Set Recommended Prices</h3>
          <div className={styles.divider} />
          <div className={styles.row}>
            <div className={styles.dropdownContainer}>
              <Dropdown
                label="Price Point 1"
                id="pricePoint1"
                options={defaultDropdownPriceOptions}
                redBackground={dropdownOneRequiresPrice}
                hidePlaceholder={false}
                disabled={!!(availablePrices && availablePrices.length < 1)}
                errorText={dropdownOneError()}
                value={
                  initialFormValues.pricePoint1 !== formValues.pricePoint1 ? undefined : initialFormValues.pricePoint1
                }
                onChange={handleDropdownSelect}
              />
            </div>
            <div className={styles.dropdownContainer}>
              <Dropdown
                label="Price Point 2"
                id="pricePoint2"
                options={defaultDropdownPriceOptions}
                redBackground={dropdownTwoRequiresPrice}
                hidePlaceholder={false}
                disabled={!!(availablePrices && availablePrices.length < 2)}
                errorText={dropdownTwoError()}
                value={
                  initialFormValues.pricePoint2 !== formValues.pricePoint2 ? undefined : initialFormValues.pricePoint2
                }
                onChange={handleDropdownSelect}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.dropdownContainer}>
              <Dropdown
                label="Price Point 3"
                id="pricePoint3"
                options={defaultDropdownPriceOptions}
                redBackground={dropdownThreeRequiresPrice}
                hidePlaceholder={false}
                disabled={!!(availablePrices && availablePrices.length < 3)}
                errorText={dropdownThreeError()}
                value={
                  initialFormValues.pricePoint3 !== formValues.pricePoint3 ? undefined : initialFormValues.pricePoint3
                }
                onChange={handleDropdownSelect}
              />
            </div>
            <div className={styles.dropdownContainer}>
              <Dropdown
                label="Price Point 4"
                id="pricePoint4"
                options={defaultDropdownPriceOptions}
                redBackground={dropdownFourRequiresPrice}
                hidePlaceholder={false}
                disabled={!!(availablePrices && availablePrices.length < 4)}
                errorText={dropdownFourError()}
                value={
                  initialFormValues.pricePoint4 !== formValues.pricePoint4 ? undefined : initialFormValues.pricePoint4
                }
                onChange={handleDropdownSelect}
              />
            </div>
          </div>
        </Card>
        <Card>
          <div className={styles.tableHeader}>
            <h3>Set Upcharges</h3>
          </div>
          <Table
            enableRowSelection={false}
            loadingRowCount={7}
            includeMinWidth={false}
            width="10px"
            height="200px"
            data={data}
            meta={{
              data,
              columns,
              editedRows,
              setEditedRows,
              cancelEdition,
              setCancelEdition,
              validRows,
              setValidRows,
              revertData: (rowIndex: number, revert: boolean) => {
                if (revert) {
                  setData((old) => old.map((row, index) => (index === rowIndex ? originalData[rowIndex] : row)));
                } else {
                  setOriginalData((old) => old.map((row, index) => (index === rowIndex ? data[rowIndex] : row)));
                }
              },
              updateData: (rowIndex: number, columnId: string, value: string, isValid: boolean) => {
                setData((old) =>
                  old.map((row, index) => {
                    if (index === rowIndex) {
                      return {
                        ...old[rowIndex],
                        [columnId]: value,
                      };
                    }
                    return row;
                  }),
                );
                setValidRows((old: Record<string, unknown>[]) => ({
                  ...old,
                  [rowIndex]: { ...old[rowIndex], [columnId]: isValid },
                }));
              },
            }}
            columns={columns}
          />
        </Card>
      </div>
    </div>
  );
};
